import "./styles.css";

const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const apiBase = isLocal ? "http://127.0.0.1:8788" : "https://rift-ws.cording.ai";
const wsUrl = isLocal ? "ws://127.0.0.1:8788/ws" : "wss://rift-ws.cording.ai/ws";
const $ = (id) => document.getElementById(id);
const points = [];
const maxPoints = 120;
let reconnectTimer;
let socket;
let lastPayloadAt = 0;

const currency = new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 });
const kstClock = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

function text(id, value) {
  const node = $(id);
  if (node) node.textContent = value;
}

function valueOrDash(value, formatter = String) {
  return value === null || value === undefined ? "—" : formatter(value);
}

function shortRunId(value) {
  if (!value) return "—";
  return value.length > 16 ? `${value.slice(0, 13)}…` : value;
}

function formatDuration(ms) {
  if (!Number.isFinite(ms)) return "—";
  const total = Math.floor(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatTime(ms) {
  return Number.isFinite(ms) ? kstClock.format(new Date(ms)) : "—";
}

function setConnection(state, label) {
  const node = $("connection");
  node.className = `connection ${state}`;
  node.querySelector("span").textContent = label;
}

function logEvent(level, message) {
  const list = $("event-log");
  const item = document.createElement("li");
  item.className = level;
  const now = kstClock.format(new Date());
  item.innerHTML = `<time>${now}</time><i></i><span></span>`;
  item.querySelector("span").textContent = message;
  list.prepend(item);
  while (list.children.length > 7) list.lastElementChild.remove();
}

function renderModels(models = []) {
  if (!models.length) return;
  const container = $("model-grid");
  container.replaceChildren(...models.map((model) => {
    const article = document.createElement("article");
    article.className = "model-card";
    const kind = document.createElement("span");
    const name = document.createElement("b");
    const status = document.createElement("small");
    kind.textContent = model.kind;
    name.textContent = model.name;
    status.textContent = model.score == null ? model.state : `${model.state} · ${model.score.toFixed(4)}`;
    article.append(kind, name, status);
    return article;
  }));
}

function render(status) {
  if (!status || status.schemaVersion !== 1) return;
  lastPayloadAt = Date.now();
  text("mode", status.mode);
  text("venue", status.market.venue === "UNCONFIGURED" ? "미연결" : status.market.venue);
  text("symbol", status.market.symbol === "UNCONFIGURED" ? "미설정" : status.market.symbol);
  text("run-id", shortRunId(status.runId));
  text("mid-price", valueOrDash(status.market.mid, currency.format));
  text("spread", valueOrDash(status.market.spreadBps, (v) => `${v.toFixed(2)} bps`));
  text("feed-state", status.connection.marketFeed.replaceAll("_", " ").toLowerCase());
  text("book-sync", status.market.bookSynchronized ? "SYNCED" : "WAIT");
  text("last-update-id", status.market.lastUpdateId == null ? "snapshot 대기" : `update ${status.market.lastUpdateId}`);
  text("depth-events", (status.market.depthEventsSeen || 0).toLocaleString());
  text("depth-unknown", `원인 미확정 감소 ${(status.market.depthDecreasesUnknown || 0).toLocaleString()}`);
  text("agg-trades", (status.market.aggregateTradesSeen || 0).toLocaleString());
  text("sequence-health", `gap ${status.market.sequenceGaps || 0} · drop ${status.market.rawEventsDropped || 0}`);
  const flow = status.flow;
  if (flow) {
    text("flow-state", flow.state);
    text("ofi-innovation", valueOrDash(flow.ofiInnovation, (v) => `${v >= 0 ? "+" : ""}${v.toFixed(4)}`));
    text("expected-ofi", valueOrDash(flow.expectedOfi, (v) => `${v >= 0 ? "+" : ""}${v.toFixed(4)}`));
    text("touch-trades", `${(flow.touchTrades || 0).toLocaleString()} / ${(flow.offTouchTrades || 0).toLocaleString()}`);
    text("flow-episodes", `${flow.activeEpisodes || 0} / ${(flow.completedEpisodes || 0).toLocaleString()}`);
    text("flow-depletions", `${(flow.observableDepletions || 0).toLocaleString()} · censored ${(flow.censoredEpisodes || 0).toLocaleString()}`);
    text("refill-ratio", valueOrDash(flow.meanRefillRatio, (v) => `${v.toFixed(3)}×`));
    text("refill-half-life", valueOrDash(flow.meanRefillHalfLifeMs, (v) => `${v.toFixed(1)} ms`));
    const latest = flow.latest;
    text("flow-latest", latest
      ? `${latest.aggressor} ${currency.format(latest.price)} · ${latest.outcome} · refill ${valueOrDash(latest.refillRatio, (v) => `${v.toFixed(2)}×`)} · signed impact ${latest.signedImpactBps.toFixed(3)} bps`
      : "완료된 episode 대기 · 관측 불가능한 상태는 CENSORED 처리");
  }
  text("authority", status.risk.authority.replaceAll("_", " "));
  text("live-orders", status.risk.liveOrdersEnabled ? "ENABLED" : "DISABLED");
  $("live-orders").className = status.risk.liveOrdersEnabled ? "on" : "off";
  text("kill-switch", status.risk.killSwitch ? "ARMED" : "RELEASED");
  $("kill-switch").className = status.risk.killSwitch ? "on" : "off";
  text("open-orders", String(status.trading.openOrders));
  text("position", status.trading.position.toFixed(2));
  text("risk-reason", status.risk.reason);
  text("ws-clients", String(status.connection.wsClients));
  text("uptime", formatDuration(status.uptimeMs));
  text("sqlite", `${status.persistence.intervalSecs}s / WAL`);
  text("raw-capture", status.market.rawEventsDropped ? "DEGRADED" : "LOSSLESS");
  text("sequence-state", status.market.sequenceGaps ? `${status.market.sequenceGaps} RESYNC` : "CONTIGUOUS");
  text("last-snapshot", formatTime(status.persistence.lastSnapshotMs));
  text("version", status.version);
  text("footer-version", status.version);
  text("event-age", valueOrDash(status.market.lastEventAgeMs, (v) => `${v} ms`));
  text("last-update", `LAST UPDATE ${formatTime(status.timestampMs)}`);
  const backend = $("backend-state");
  backend.className = status.connection.backendReady ? "ready" : "";
  backend.innerHTML = `<i></i>${status.connection.backendReady ? "READY" : "WAITING"}`;
  renderModels(status.models);

  if (Number.isFinite(status.market.mid)) {
    points.push(status.market.mid);
    if (points.length > maxPoints) points.shift();
    $("chart-empty").hidden = true;
    drawChart();
  }
}

function drawChart() {
  const canvas = $("market-chart");
  const box = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(box.width * dpr));
  canvas.height = Math.max(1, Math.round(box.height * dpr));
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);
  const width = box.width;
  const height = box.height;
  ctx.clearRect(0, 0, width, height);
  if (points.length < 2) return;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const x = (index) => 12 + index * ((width - 24) / (points.length - 1));
  const y = (value) => 16 + (max - value) * ((height - 32) / range);
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, "rgba(93, 239, 199, .28)");
  gradient.addColorStop(1, "rgba(93, 239, 199, 0)");

  ctx.beginPath();
  ctx.moveTo(x(0), y(points[0]));
  points.forEach((point, index) => ctx.lineTo(x(index), y(point)));
  ctx.lineTo(x(points.length - 1), height);
  ctx.lineTo(x(0), height);
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.beginPath();
  points.forEach((point, index) => index ? ctx.lineTo(x(index), y(point)) : ctx.moveTo(x(index), y(point)));
  ctx.strokeStyle = "#5defc7";
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

async function fetchStatus() {
  try {
    const response = await fetch(`${apiBase}/api/status`, { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    render(await response.json());
  } catch (error) {
    if (!socket || socket.readyState !== WebSocket.OPEN) setConnection("error", "백엔드 대기");
  }
}

function connect() {
  clearTimeout(reconnectTimer);
  setConnection("connecting", "연결 중");
  socket = new WebSocket(wsUrl);
  socket.addEventListener("open", () => {
    setConnection("online", "실시간 연결");
    logEvent("ok", "RIFT WebSocket 연결");
  });
  socket.addEventListener("message", (event) => {
    try { render(JSON.parse(event.data)); } catch { logEvent("warn", "해석할 수 없는 상태 메시지"); }
  });
  socket.addEventListener("close", () => {
    setConnection("error", "재연결 대기");
    logEvent("warn", "WebSocket 연결 종료 — 3초 후 재시도");
    reconnectTimer = setTimeout(connect, 3000);
  });
  socket.addEventListener("error", () => socket.close());
}

$("clear-log").addEventListener("click", () => $("event-log").replaceChildren());
window.addEventListener("resize", drawChart);
setInterval(() => text("clock", `${kstClock.format(new Date())} KST`), 1000);
setInterval(() => {
  if (Date.now() - lastPayloadAt > 5000) fetchStatus();
}, 5000);
text("clock", `${kstClock.format(new Date())} KST`);
logEvent("info", `Dashboard 시작 · ${isLocal ? "LOCAL" : "CLOUDFLARE"}`);
fetchStatus();
connect();
